import { create } from 'zustand'
import { 
  ConversationListItem, 
  ConversationDetail, 
  ConversationFilters 
} from '@/services/conversationsApi'

interface ConversationsState {
  // Lista de conversaciones
  conversations: ConversationListItem[]
  totalPages: number
  currentPage: number
  isLoading: boolean
  error: string | null
  
  // Conversación activa
  activeConversation: ConversationDetail | null
  isLoadingConversation: boolean
  conversationError: string | null
  
  // Filtros
  filters: ConversationFilters
  
  // Acciones
  setConversations: (conversations: ConversationListItem[], totalPages: number, currentPage: number) => void
  setActiveConversation: (conversation: ConversationDetail | null) => void
  setFilters: (filters: ConversationFilters) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
  setConversationLoading: (isLoading: boolean) => void
  setConversationError: (error: string | null) => void
  
  // Actualizar conversación específica
  updateConversation: (conversationId: string, updates: Partial<ConversationListItem>) => void
  
  // Agregar mensaje a conversación activa
  addMessageToActiveConversation: (message: ConversationDetail['messages'][0]) => void
  
  // Actualizar status/mode de conversación
  updateConversationStatus: (conversationId: string, status: 'open' | 'closed') => void
  updateConversationMode: (conversationId: string, mode: 'bot' | 'human') => void
  
  // Reset
  reset: () => void
}

const initialState = {
  conversations: [],
  totalPages: 0,
  currentPage: 1,
  isLoading: false,
  error: null,
  activeConversation: null,
  isLoadingConversation: false,
  conversationError: null,
  filters: {
    status: 'open' as const,
    mode: 'all' as const,
    channelType: 'all',
    page: 1,
    limit: 20,
  },
}

export const useConversationsStore = create<ConversationsState>((set, get) => ({
  ...initialState,
  
  setConversations: (conversations, totalPages, currentPage) => set({
    conversations,
    totalPages,
    currentPage,
  }),
  
  setActiveConversation: (activeConversation) => set({
    activeConversation,
  }),
  
  setFilters: (filters) => set({
    filters: { ...get().filters, ...filters },
  }),
  
  setLoading: (isLoading) => set({ isLoading }),
  
  setError: (error) => set({ error }),
  
  setConversationLoading: (isLoadingConversation) => set({ isLoadingConversation }),
  
  setConversationError: (conversationError) => set({ conversationError }),
  
  updateConversation: (conversationId, updates) => set((state) => ({
    conversations: state.conversations.map((conv) =>
      conv.id === conversationId ? { ...conv, ...updates } : conv
    ),
  })),
  
  addMessageToActiveConversation: (message) => set((state) => {
    if (!state.activeConversation) return state
    
    return {
      activeConversation: {
        ...state.activeConversation,
        messages: [...state.activeConversation.messages, message],
      },
    }
  }),
  
  updateConversationStatus: (conversationId, status) => set((state) => {
    const updatedConversations = state.conversations.map((conv) =>
      conv.id === conversationId ? { ...conv, status } : conv
    )
    
    const updatedActiveConversation = 
      state.activeConversation?.id === conversationId
        ? { ...state.activeConversation, status }
        : state.activeConversation
    
    return {
      conversations: updatedConversations,
      activeConversation: updatedActiveConversation,
    }
  }),
  
  updateConversationMode: (conversationId, mode) => set((state) => {
    const updatedConversations = state.conversations.map((conv) =>
      conv.id === conversationId ? { ...conv, mode } : conv
    )
    
    const updatedActiveConversation = 
      state.activeConversation?.id === conversationId
        ? { ...state.activeConversation, mode }
        : state.activeConversation
    
    return {
      conversations: updatedConversations,
      activeConversation: updatedActiveConversation,
    }
  }),
  
  reset: () => set(initialState),
}))

// Selectores útiles
export const useConversationsList = () => useConversationsStore((state) => ({
  conversations: state.conversations,
  totalPages: state.totalPages,
  currentPage: state.currentPage,
  isLoading: state.isLoading,
  error: state.error,
  filters: state.filters,
}))

export const useActiveConversation = () => useConversationsStore((state) => ({
  conversation: state.activeConversation,
  isLoading: state.isLoadingConversation,
  error: state.conversationError,
}))

export const useConversationsActions = () => useConversationsStore((state) => ({
  setConversations: state.setConversations,
  setActiveConversation: state.setActiveConversation,
  setFilters: state.setFilters,
  setLoading: state.setLoading,
  setError: state.setError,
  setConversationLoading: state.setConversationLoading,
  setConversationError: state.setConversationError,
  updateConversation: state.updateConversation,
  addMessageToActiveConversation: state.addMessageToActiveConversation,
  updateConversationStatus: state.updateConversationStatus,
  updateConversationMode: state.updateConversationMode,
  reset: state.reset,
}))
